import React, { useRef, useState, useLayoutEffect, useEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { ConfirmationModal } from "./ConfirmationModal";

const DropdownTrigger = styled.div`
  width: 100%;
  min-height: 30px;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #fff;
  font-weight: 300;
  font-size: 15px;
  font-family: "Arial Black", "Segoe UI", sans-serif;
  cursor: pointer;
  padding: 0px 4px;
  box-sizing: border-box;
`;

const TextWrapper = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 0;
  font-size: 15px;
  font-family: "Arial Black", "Segoe UI", sans-serif;
  gap: 6px;
  max-width: 100%;
  flex-wrap: nowrap;
`;

const TextLabel = styled.span`
  font-size: 14px;
  max-width: 100%;
  white-space: normal;
  overflow: visible;
  display: block;
  line-height: 1.4;

  strong {
    font-weight: 900;
    font-size: 17px;
  }
`;

const PortalDropdown = styled.div`
  position: absolute;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  min-width: 180px;
  z-index: 3000;
  padding: 4px 0;

  /* NOVO: limita altura em mobile e desktop */
  max-height: calc(100vh - 16px);
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  background-color: ${(props) => (props.active ? "#EE754C" : "white")};
  color: ${(props) => (props.active ? "white" : "#99B0AD")};
  font-weight: ${(props) => (props.active ? "bold" : "normal")};

  &:hover {
    background-color: #f0f0f0;
  }
`;

const StatusDropdown = ({
  reservation,
  allowedStatuses,
  onSelectStatus,
  getStatusColor,
  getStatusLabel,
}) => {
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useLayoutEffect(() => {
    if (visible && wrapperRef.current && dropdownRef.current) {
      const triggerRect = wrapperRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const margin = 4;

      let top = triggerRect.bottom + window.scrollY;
      let left = triggerRect.left + window.scrollX;

      if (left + dropdownRect.width > viewportW + window.scrollX) {
        left = viewportW - dropdownRect.width - margin;
      }
      if (left < margin) left = margin;

      if (top + dropdownRect.height > viewportH + window.scrollY - margin) {
        top = triggerRect.top - dropdownRect.height + window.scrollY;

        if (top < window.scrollY + margin) {
          top = window.scrollY + margin;
        }
      }

      setCoords({ top, left });
    }
  }, [visible]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setVisible(false);
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible]);

  const handleDropdownItemClick = (status) => {
    if (status !== reservation.status) {
      setPendingStatus(status);
      setShowModal(true);
    }
  };

  const handleConfirm = () => {
    onSelectStatus(pendingStatus);
    setShowModal(false);
    setVisible(false);
    setPendingStatus(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setPendingStatus(null);
  };

  return (
    <>
      <DropdownTrigger
        ref={wrapperRef}
        onClick={() => setVisible((prev) => !prev)}
        color={getStatusColor(reservation.status)}
      >
        {reservation.waiting_list_number != null && (
          <>{reservation.waiting_list_number}º </>
        )}
        <TextWrapper>
          <TextLabel>{getStatusLabel(reservation.status)}</TextLabel>
          <FontAwesomeIcon
            icon={visible ? faChevronUp : faChevronDown}
            size="sm"
          />
        </TextWrapper>
      </DropdownTrigger>

      {visible &&
        createPortal(
          <PortalDropdown
            ref={dropdownRef}
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
            }}
          >
            {allowedStatuses.map((status) => (
              <DropdownItem
                key={status}
                active={status === reservation.status}
                style={{
                  pointerEvents:
                    status === reservation.status ? "none" : "auto",
                  opacity: status === reservation.status ? 0.6 : 1,
                }}
                onClick={() => {
                  handleDropdownItemClick(status);
                  setVisible(false);
                }}
              >
                {getStatusLabel(status)}
              </DropdownItem>
            ))}
          </PortalDropdown>,
          document.body,
        )}

      <ConfirmationModal
        show={showModal}
        onHide={handleCancel}
        onConfirm={handleConfirm}
        title="Alterar estado"
        body={
          <>
            Tens a certeza que pretendes alterar o estado da reserva para{" "}
            <strong>{getStatusLabel(pendingStatus)}</strong>?
          </>
        }
        confirmLabel="Alterar estado"
        cancelLabel="Cancelar"
        confirmColor="#EE754C"
        cancelColor="#99B0AD"
      />
    </>
  );
};

export default StatusDropdown;
